import {
  DISPLAY_BASIS_INCL,
  deriveUnitPriceExclVat,
  displayUnitPrice,
  roundMoney,
} from './moneyUtils.js';

const CHILD_PREFIX_RE = /^\s*↳\s*/;

const ADDONS_LABEL = 'Extra opties';

/** How line prices are shown on the customer-facing quote (PDF / public preview). */
export const PRICE_VISIBILITY_LINE_ITEMS = 'line_items';
export const PRICE_VISIBILITY_MAIN_PRODUCTS = 'main_products';
export const PRICE_VISIBILITY_CONFIGURATION_SUBTOTALS = 'configuration_subtotals';
export const PRICE_VISIBILITY_GRAND_TOTAL = 'grand_total';

export const PRICE_VISIBILITY_VALUES = [
  PRICE_VISIBILITY_LINE_ITEMS,
  PRICE_VISIBILITY_MAIN_PRODUCTS,
  PRICE_VISIBILITY_CONFIGURATION_SUBTOTALS,
  PRICE_VISIBILITY_GRAND_TOTAL,
];

/**
 * Internal cost lines: charged to the customer but never shown as their own
 * row. Their amount stays in the stored totals, so a customer-facing surface
 * must run the lines through `absorbHiddenLines`, not just drop them.
 */
export function isHiddenLine(item) {
  // Proposal items live in a JSONB blob, so the flag sits on the item itself.
  // Invoice and project items are relational rows without a `hidden` column,
  // so there the flag travels in metadata.
  return item?.hidden === true || item?.metadata?.hidden === true;
}

/**
 * Drop internal cost lines without touching any other line. Only safe where
 * no amounts are rendered (packing slips, work orders). On priced documents
 * use `absorbHiddenLines`, or the visible amounts stop adding up to the total.
 */
export function filterCustomerVisibleItems(items) {
  const list = Array.isArray(items) ? items : [];
  return list.filter((item) => !isHiddenLine(item));
}

/** Net excl-VAT amount of a single line, in the document's display basis. */
function lineNetExclVat(item, displayBasis) {
  const quantity = Number(item?.quantity) || 0;
  return roundMoney(quantity * deriveUnitPriceExclVat(item, displayBasis));
}

function lineVatRate(item) {
  return Number(item?.vat_rate) || 0;
}

/**
 * The visible line that absorbs a hidden amount: the first priced main product
 * carrying the same VAT rate. Rate has to match, otherwise a hidden 21% cost
 * folded into a 9% line would move the mismatch to the VAT row instead of
 * closing it.
 */
function findCarrierIndex(visibleItems, vatRate) {
  return visibleItems.findIndex(
    (item) =>
      isPricedLine(item) &&
      !isAddonLine(item) &&
      Number(item?.quantity) > 0 &&
      lineVatRate(item) === vatRate
  );
}

/**
 * Work out, per VAT rate, which visible line absorbs the hidden amounts.
 * `unabsorbed` is the amount that has nowhere to go: no visible main product
 * shares its VAT rate. That amount would silently break the document, so the
 * editor blocks on it.
 */
export function resolveHiddenAbsorption(items, displayBasis = DISPLAY_BASIS_INCL) {
  const list = Array.isArray(items) ? items : [];
  const visible = list.filter((item) => !isHiddenLine(item));

  const hiddenByRate = new Map();
  for (const item of list) {
    if (!isHiddenLine(item) || !isPricedLine(item)) continue;
    const rate = lineVatRate(item);
    const net = lineNetExclVat(item, displayBasis);
    hiddenByRate.set(rate, roundMoney((hiddenByRate.get(rate) || 0) + net));
  }

  const assignments = [];
  let unabsorbed = 0;

  for (const [rate, amount] of hiddenByRate) {
    if (amount === 0) continue;
    const carrierIndex = findCarrierIndex(visible, rate);
    if (carrierIndex === -1) {
      unabsorbed = roundMoney(unabsorbed + amount);
      continue;
    }
    assignments.push({ vat_rate: rate, amount, carrierIndex });
  }

  return { visible, assignments, unabsorbed };
}

/**
 * Customer-facing line list: hidden lines disappear as rows and their amount
 * is folded into a visible main product's price, the same way `main_products`
 * rolls addon totals into their parent. This keeps the rendered column adding
 * up to the stored subtotal, which plain filtering does not.
 *
 * Folding happens at render time and is never stored: the carrier is usually a
 * configurator line that a revision rebuilds from scratch, so a baked-in bump
 * would vanish while the hidden line itself survives.
 */
export function absorbHiddenLines(items, displayBasis = DISPLAY_BASIS_INCL) {
  const { visible, assignments } = resolveHiddenAbsorption(items, displayBasis);
  if (assignments.length === 0) return visible;

  const result = visible.slice();

  for (const { amount, carrierIndex } of assignments) {
    const carrier = result[carrierIndex];
    const quantity = Number(carrier.quantity) || 1;
    const net = lineNetExclVat(carrier, displayBasis);
    // Excl-VAT unit prices keep 4 decimals, so quantity * unit still rounds
    // back to the exact line total even when the amount does not divide.
    const unitExclVat = roundMoney((net + amount) / quantity);

    result[carrierIndex] = {
      ...carrier,
      unit_price_excl_vat: unitExclVat,
      unit_price: displayUnitPrice({ ...carrier, unit_price_excl_vat: unitExclVat }, displayBasis),
    };
  }

  return result;
}

/**
 * Lines that `generateProposalItemsFromEnriched` rebuilds from the configurator
 * submission. Every such line carries either an `enriched_ref` (main products,
 * options, upsells) or a top-level `product_index` (auto-includes and
 * cross-option rule lines); the commercial cap line is regenerated too.
 */
export function isEnrichedDerivedLine(item) {
  const meta = item?.metadata || {};
  if (meta.enriched_ref) return true;
  if (meta.product_index != null) return true;
  if (meta.source === 'commercial_cap') return true;
  return false;
}

/**
 * Lines a human added in the proposal editor. A revision regenerates the
 * configurator lines from scratch, so these have to be carried over by hand or
 * they vanish — silently, in the case of hidden cost lines.
 */
export function filterAuthoredLines(items) {
  const list = Array.isArray(items) ? items : [];
  return list.filter((item) => item && !isEnrichedDerivedLine(item));
}

/**
 * Remove the legacy nested-line prefix from a proposal/invoice description.
 */
export function stripChildPrefix(description) {
  if (typeof description !== 'string') return '';
  return description.replace(CHILD_PREFIX_RE, '').trimStart();
}

function hasChildPrefix(description) {
  return typeof description === 'string' && CHILD_PREFIX_RE.test(description);
}

/**
 * Child quote lines: upsells, options, and auto-includes shown under a product.
 * Auto-includes belong to their parent product; treating them as a new parent
 * would nest later upsells under the auto-include instead.
 */
export function isAddonLine(item) {
  if (!item) return false;
  if (item.item_type === 'text' || item.item_type === 'discount') return false;

  const kind = item.metadata?.line_kind;
  if (kind === 'upsell' || kind === 'option' || kind === 'auto_include') return true;

  if (item.metadata?.source === 'auto_include') return true;

  const ref = item.metadata?.enriched_ref;
  if (ref?.upsell_id) return true;
  if (ref?.option_id) return true;

  return hasChildPrefix(item.description);
}

/**
 * Annotate document lines for nested Extra-opties rendering without mutating source items.
 */
export function annotateLineItems(items) {
  const list = Array.isArray(items) ? items : [];

  return list.map((item, index) => {
    const isAddon = isAddonLine(item);
    const prevIsAddon = index > 0 && isAddonLine(list[index - 1]);
    const nextIsAddon = index < list.length - 1 && isAddonLine(list[index + 1]);

    return {
      ...item,
      isAddon,
      isFirstAddon: isAddon && !prevIsAddon,
      isLastAddon: isAddon && !nextIsAddon,
      displayDescription: stripChildPrefix(item.description) || item.description || '',
    };
  });
}

/**
 * Normalize a raw price_visibility value. Unknown / null → line_items.
 */
export function normalizePriceVisibility(value) {
  if (PRICE_VISIBILITY_VALUES.includes(value)) return value;
  return PRICE_VISIBILITY_LINE_ITEMS;
}

/**
 * Resolve price visibility: proposal → form defaults → org defaults → line_items.
 * null / omitted at any level means inherit from the parent.
 */
export function resolveProposalPriceVisibility({
  proposal = {},
  formDefaults = {},
  orgDefaults = {},
} = {}) {
  const fromProposal = proposal?.price_visibility ?? proposal?.priceVisibility;
  if (fromProposal != null && fromProposal !== '') {
    return normalizePriceVisibility(fromProposal);
  }

  const fromForm = formDefaults?.price_visibility ?? formDefaults?.priceVisibility;
  if (fromForm != null && fromForm !== '') {
    return normalizePriceVisibility(fromForm);
  }

  const fromOrg = orgDefaults?.price_visibility ?? orgDefaults?.priceVisibility;
  if (fromOrg != null && fromOrg !== '') {
    return normalizePriceVisibility(fromOrg);
  }

  return PRICE_VISIBILITY_LINE_ITEMS;
}

/** Whether the price / line-total table columns should be rendered. */
export function showPriceColumns(mode) {
  return normalizePriceVisibility(mode) !== PRICE_VISIBILITY_GRAND_TOTAL;
}

function isInvoiceDeductionRow(row, getLineTotal) {
  if (row.isAddon || row.item_type === 'text') return false;
  return (
    row.item_type === 'discount' ||
    row.item_type === 'deposit_deduction' ||
    Number(getLineTotal(row)) < 0
  );
}

/**
 * Invoice variant of applyPriceVisibility. Deductions (discounts, settled
 * deposits) always keep their amount: hiding them leaves an invoice whose
 * visible amounts no longer explain the total due. line_items keeps every
 * non-text row priced, as invoices have always rendered.
 *
 * @returns {{ rows: Array, columns: { unitPrice: boolean, vat: boolean, lineTotal: boolean } }}
 */
export function applyInvoicePriceVisibility(annotatedItems, mode, options = {}) {
  const visibility = normalizePriceVisibility(mode);
  const getLineTotal =
    typeof options.getLineTotal === 'function'
      ? options.getLineTotal
      : (item) => Number(item.line_total ?? 0);

  const unitPriceColumn =
    visibility === PRICE_VISIBILITY_LINE_ITEMS ||
    visibility === PRICE_VISIBILITY_CONFIGURATION_SUBTOTALS;

  const rows = applyPriceVisibility(annotatedItems, visibility, { getLineTotal }).map((row) => {
    if (row.item_type === 'text') return row;
    if (visibility === PRICE_VISIBILITY_LINE_ITEMS || isInvoiceDeductionRow(row, getLineTotal)) {
      return {
        ...row,
        showUnitPrice: unitPriceColumn,
        showLineTotal: true,
        displayLineTotal: getLineTotal(row),
        ...(isInvoiceDeductionRow(row, getLineTotal) && {
          showConfigurationSubtotal: false,
          configurationSubtotal: null,
          configurationSubtotalLabel: null,
        }),
      };
    }
    return row;
  });

  const columns = {
    unitPrice: unitPriceColumn,
    vat: visibility !== PRICE_VISIBILITY_GRAND_TOTAL,
    lineTotal: visibility !== PRICE_VISIBILITY_GRAND_TOTAL || rows.some((row) => row.showLineTotal),
  };

  return { rows, columns };
}

function isPricedLine(item) {
  return item && item.item_type !== 'text' && item.item_type !== 'discount';
}

/**
 * Apply customer-facing price visibility flags to annotated line items.
 * Does not mutate stored prices — only presentation fields.
 *
 * @param {Array} annotatedItems - output of annotateLineItems
 * @param {string} mode - price_visibility value
 * @param {{ getLineTotal?: (item) => number }} [options]
 * @returns {Array} items with showUnitPrice, showLineTotal, displayLineTotal
 */
export function applyPriceVisibility(annotatedItems, mode, options = {}) {
  const visibility = normalizePriceVisibility(mode);
  const list = Array.isArray(annotatedItems) ? annotatedItems : [];
  const getLineTotal =
    typeof options.getLineTotal === 'function'
      ? options.getLineTotal
      : (item) => Number(item.displayLineTotal ?? item.line_total ?? 0);

  if (visibility === PRICE_VISIBILITY_GRAND_TOTAL) {
    return list.map((item) => ({
      ...item,
      showUnitPrice: false,
      showLineTotal: false,
      displayLineTotal: null,
    }));
  }

  if (
    visibility === PRICE_VISIBILITY_LINE_ITEMS ||
    visibility === PRICE_VISIBILITY_CONFIGURATION_SUBTOTALS
  ) {
    const showSubtotals = visibility === PRICE_VISIBILITY_CONFIGURATION_SUBTOTALS;
    const result = list.map((item) => {
      const priced = isPricedLine(item);
      return {
        ...item,
        showUnitPrice: priced,
        showLineTotal: priced,
        displayLineTotal: priced ? getLineTotal(item) : null,
        showConfigurationSubtotal: false,
        configurationSubtotal: null,
        configurationSubtotalLabel: null,
      };
    });

    if (!showSubtotals) return result;

    for (let i = 0; i < list.length; i++) {
      if (list[i].isAddon || !isPricedLine(list[i])) continue;
      let end = i;
      let subtotal = getLineTotal(list[i]);
      for (let j = i + 1; j < list.length; j++) {
        if (!list[j].isAddon) break;
        end = j;
        if (isPricedLine(list[j])) subtotal += getLineTotal(list[j]);
      }
      const name = list[i].displayDescription || list[i].description || '';
      result[end] = {
        ...result[end],
        showConfigurationSubtotal: true,
        configurationSubtotal: subtotal,
        configurationSubtotalLabel: name ? `Subtotaal ${name}` : 'Subtotaal',
      };
    }

    return result;
  }

  // main_products: hide unit prices; roll addon totals into the parent line total.
  const result = list.map((item) => {
    if (!isPricedLine(item)) {
      return {
        ...item,
        showUnitPrice: false,
        showLineTotal: false,
        displayLineTotal: null,
      };
    }
    if (item.isAddon) {
      return {
        ...item,
        showUnitPrice: false,
        showLineTotal: false,
        displayLineTotal: null,
      };
    }
    return {
      ...item,
      showUnitPrice: false,
      showLineTotal: true,
      displayLineTotal: getLineTotal(item),
    };
  });

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (!item.showLineTotal || item.isAddon) continue;

    let rolled = getLineTotal(list[i]);
    for (let j = i + 1; j < result.length; j++) {
      if (!result[j].isAddon) break;
      if (!isPricedLine(list[j])) continue;
      rolled += getLineTotal(list[j]);
    }
    result[i] = { ...item, displayLineTotal: rolled };
  }

  return result;
}

export { ADDONS_LABEL };
